// Filename: src/screens/report/Report.jsx
// Last Updated: 2025-03-22 08:20:48
// Author: raghavkunkee

import { FilterMatchMode } from "primereact/api";
import { Avatar } from "primereact/avatar";
import { Toast } from "primereact/toast";
import { useEffect, useState, useCallback, useContext, useRef } from "react";
import { subscriberContext } from "../../App";
import moment from "moment";

import { ButtonGeneric, ButtonRefresh } from "../../components/generic/button";
import Loading from "../../components/generic/loading";
import { StyledLabelBlack } from "../../components/routing/Styled";
import { StageLabel } from "../../containers/Styled";
import ReportTable from "../../containers/table/ReportTable";
import Pagination from "../books/Pagination";
import { fetchWithFallback, reportFieldGroups } from "../../utils/apiUtils";

/**
 * Report component for displaying and managing report files
 * @return {JSX.Element} Rendered component
 */
const Report = () => {
  const { keycloak } = useContext(subscriberContext);
  const toast = useRef(null);

  // State management
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const [lastPage, setLastPage] = useState(1);
  const [dataMain, setDataMain] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(false);
  const [totalReports, setTotalReports] = useState(0);
  const [downloadInProgress, setDownloadInProgress] = useState(false);

  const sizeList = [
    { name: 20, value: 20 },
    { name: 30, value: 30 },
    { name: 40, value: 40 },
  ];

  const [filters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    "requestFile.originalFileName": {
      value: null,
      matchMode: FilterMatchMode.CONTAINS,
    },
    submittedBy: { value: null, matchMode: FilterMatchMode.CONTAINS },
    submitTime: { value: null, matchMode: FilterMatchMode.CONTAINS },
    processingStatus: { value: null, matchMode: FilterMatchMode.CONTAINS },
    reportType: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  /**
   * Fetches report data from the API
   */
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const url = `${process.env.REACT_APP_APIIMPORTURL}/v1/requests/report?page=${page - 1}&size=${size}`;
      const data = await fetchWithFallback(
        url, 
        { _embedded: { responseVOList: [] }, page: { totalPages: 1, totalElements: 0 } }, 
        {}, 
        keycloak?.token
      );
      
      setLastPage(data?.page?.totalPages || 1);
      setDataMain(data._embedded?.responseVOList || []);
      setTotalReports(data?.page?.totalElements || 0);
      // Show success toast only when refreshing (not on initial load)
      if (refreshTrigger) {
        toast.current.show({
          severity: 'success',
          summary: 'Refreshed',
          detail: 'Report data has been updated',
          life: 3000
        });
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Failed to load report data");
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load report data',
        life: 3000
      });
    } finally {
      setLoading(false);
    }
  }, [page, size, keycloak?.token]);

  /**
   * Handles file downloads safely across all browsers
   * @param {string} downloadUrl - URL to download the file from
   * @param {string} defaultFilename - Fallback filename if none provided in Content-Disposition
   */
  const handleDownload = useCallback((downloadUrl, defaultFilename) => {
    if (downloadInProgress) {
      toast.current.show({
        severity: 'info',
        summary: 'Download in Progress',
        detail: 'Please wait for the current download to complete',
        life: 3000
      });
      return;
    }

    setDownloadInProgress(true);
    toast.current.show({
      severity: 'info',
      summary: 'Starting Download',
      detail: 'Preparing your file for download...',
      life: 3000
    });

    // Create a progress indicator for large files
    const downloadStartTime = new Date();
    const progressToastId = toast.current.show({
      severity: 'info',
      summary: 'Download Progress',
      detail: 'Download starting...',
      life: 30000,
      sticky: true
    });

    // Use fetch API to handle the download properly
    fetch(downloadUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${keycloak?.token}`
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      // Get the filename from Content-Disposition header if available
      const contentDisposition = response.headers.get('content-disposition');
      let filename = defaultFilename || 'download';

      if (contentDisposition) {
        // Try to extract filename from various Content-Disposition formats
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(contentDisposition);
        if (matches != null && matches[1]) {
          // Remove quotes if present
          filename = matches[1].replace(/['"]/g, '');
        }
      }

      // Get file size for progress reporting
      const contentLength = response.headers.get('content-length');
      const totalSize = parseInt(contentLength, 10) || 0;

      // Use ReadableStream for efficient downloads
      const reader = response.body.getReader();
      let receivedLength = 0;
      const chunks = [];

      // Update progress every second for large files
      const updateInterval = setInterval(() => {
        if (totalSize > 0) {
          const percentComplete = Math.round((receivedLength / totalSize) * 100);
          const elapsed = (new Date() - downloadStartTime) / 1000;
          const speed = receivedLength / elapsed / 1024 / 1024; // MB/s

          toast.current.update(progressToastId, {
            severity: 'info',
            summary: `Download Progress: ${percentComplete}%`,
            detail: `${(receivedLength / 1024 / 1024).toFixed(2)} MB of ${(totalSize / 1024 / 1024).toFixed(2)} MB (${speed.toFixed(2)} MB/s)`,
            life: 30000,
            sticky: true
          });
        }
      }, 1000);

      // Process the stream
      return new Promise((resolve, reject) => {
        function processStream({ done, value }) {
          if (done) {
            clearInterval(updateInterval);
            // Combine all chunks into a single Uint8Array
            const chunksAll = new Uint8Array(receivedLength);
            let position = 0;
            for (const chunk of chunks) {
              chunksAll.set(chunk, position);
              position += chunk.length;
            }
            resolve({ data: chunksAll, filename });
            return;
          }

          // Add this chunk to our accumulated chunks
          receivedLength += value.length;
          chunks.push(value);
          
          // Continue reading
          reader.read().then(processStream).catch(reject);
        }

        reader.read().then(processStream).catch(reject);
      });
    })
    .then(({ data, filename }) => {
      // Clear the progress toast
      toast.current.clear();
      
      // Create a blob from the data
      const blob = new Blob([data]);
      const url = window.URL.createObjectURL(blob);
      
      // Create an anchor element and trigger the download
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      
      toast.current.show({
        severity: 'success',
        summary: 'Download Complete',
        detail: `Successfully downloaded ${filename}`,
        life: 3000
      });
    })
    .catch(error => {
      console.error('Download failed:', error);
      toast.current.show({
        severity: 'error',
        summary: 'Download Failed',
        detail: 'Unable to download file. Please try again.',
        life: 3000
      });
    })
    .finally(() => {
      setDownloadInProgress(false);
      toast.current.clear(); // Clear any remaining progress toasts
    });
  }, [keycloak?.token, downloadInProgress, toast]);

  /**
   * Handles refresh button click
   */
  const handleRefresh = useCallback(() => {
    setRefreshTrigger(prev => !prev);
    toast.current.show({
      severity: 'info',
      summary: 'Refreshing',
      detail: 'Refreshing report data...',
      life: 3000
    });
  }, []);

  // Cell renderers
  const imageWithText = useCallback((rowData) => (
    <div className="flex align-items-center gap-2">
      <Avatar icon="pi pi-user" size="medium" shape="circle" />
      <span>{rowData.submittedBy}</span>
    </div>
  ), []);

  const actionBody = useCallback((rowData) => (
    <>
      <ButtonGeneric
        toolpos="left"
        tooltip="Download Request File"
        extraClass="downloadGenericButton bg-primary-400"
        icon="pi pi-download"
        disabled={!rowData.requestFile || downloadInProgress}
        method={() => {
          if (rowData.requestFile?._links?.download?.href) {
            // Extract a suitable default filename
            const defaultFilename = rowData.requestFile.originalFileName || 'request-file.zip';
            handleDownload(rowData.requestFile._links.download.href, defaultFilename);
          }
        }}
      />
      <ButtonGeneric
        toolpos="left"
        tooltip="Download Response File"
        extraClass="downloadGenericButton bg-teal-400"
        icon="pi pi-download"
        disabled={!rowData.reportFile || downloadInProgress}
        method={() => {
          if (rowData.reportFile?._links?.download?.href) {
            // Create a default filename based on report type and timestamp
            const timestamp = moment().format('YYYYMMDD_HHmmss');
            const reportType = rowData.reportType || 'REPORT';
            const defaultFilename = `${reportType}_${timestamp}.zip`;
            handleDownload(rowData.reportFile._links.download.href, defaultFilename);
          }
        }}
      />
    </>
  ), [handleDownload, downloadInProgress]);

  const processingStatusBody = useCallback((rowData) => (
    <StageLabel className={`stage-badge status-${rowData.processingStatus}`}>
      {rowData.processingStatus}
    </StageLabel>
  ), []);

  const uploadBody = useCallback((rowData) => (
    <div>
      <span>{moment(rowData?.submitTime).format("DD MMM YYYY HH:mm:ss")}</span>
    </div>
  ), []);

  const fieldGroupBody = useCallback((rowData) => (
    <span>
      {reportFieldGroups[rowData.reportType] || rowData.reportType} ({rowData.marketplace})
    </span>
  ), []);

  // Duration column body renderer
  const durationBody = useCallback((rowData) => {
    // Check if stats, val, startedAt, and finishedAt exist
    if (!rowData?.stats?.val?.startedAt || !rowData?.stats?.val?.finishedAt) {
      return <span>N/A</span>;
    }

    // Calculate duration in minutes
    const startTime = moment(rowData.stats.val.startedAt);
    const endTime = moment(rowData.stats.val.finishedAt);
    
    // Calculate difference in milliseconds
    const durationMs = endTime.diff(startTime);
    
    // Convert to minutes with 2 decimal places
    const durationMinutes = (durationMs / 60000).toFixed(2);
    
    // Color coding based on duration
    let color = 'inherit';
    if (durationMinutes > 10) {
      color = '#e74c3c'; // Red for long durations
    } else if (durationMinutes > 5) {
      color = '#f39c12'; // Orange for medium durations
    } else if (durationMinutes > 0) {
      color = '#27ae60'; // Green for short durations
    }
    
    return (
      <div>
        <span style={{ color, fontWeight: durationMinutes > 5 ? 'bold' : 'normal' }}>
          {durationMinutes} min
        </span>
      </div>
    );
  }, []);

  // Table columns configuration
  const columns = [
    {
      id: "requestFile.originalFileName",
      field: "requestFile.originalFileName",
      header: "File Name",
      expander: true,
    },
    {
      id: "reportType",
      field: "reportType",
      header: "Fields",
      body: fieldGroupBody,
    },
    {
      id: "submittedBy",
      field: "submittedBy",
      header: "Requested By",
      body: imageWithText,
    },
    {
      id: "submitTime",
      field: "submitTime",
      header: "Request Date",
      body: uploadBody,
    },
    {
      id: "duration",
      field: "stats.val.timeTaken",
      header: "Duration",
      body: durationBody,
      sortable: true,
    },
    {
      field: "processingStatus",
      header: "Processing Status",
      body: processingStatusBody,
    },
    {
      id: "actionBody",
      field: "actionBody",
      header: "Action",
      body: actionBody,
    },
  ];

  // Effects
  useEffect(() => {
    fetchData();
  }, [fetchData, refreshTrigger]);

  return (
    <>
      <Toast ref={toast} position="top-right" />
      
      <div className="grid">
        <div className="col-8 mt-2 mb-2">
          <div className="flex align-items-center">
            <i className="pi pi-bars mr-2" style={{ fontSize: "1.5rem" }} />
            <StyledLabelBlack>
              Details of Report Files ({totalReports})
            </StyledLabelBlack>
          </div>
        </div>
        <div className="col-4 flex justify-content-end align-items-center mt-2 mb-2">
          <ButtonRefresh
            icon="-refresh"
            tooltip="Refresh"
            method={handleRefresh}
            extraClass="refreshButton"
            loading={loading}
          />
        </div>
      </div>

      {error && (
        <div className="p-message p-message-error mb-2">
          <i className="pi pi-exclamation-triangle"></i>
          <span className="p-message-text">{error}</span>
        </div>
      )}

      {!loading && (
        <>
          <ReportTable 
            data={dataMain} 
            columns={columns} 
            filters={filters} 
          />
          <Pagination
            page={page}
            setPage={setPage}
            size={size}
            setSize={setSize}
            lastPage={lastPage}
            sizeList={sizeList}
          />
        </>
      )}

      {loading && <Loading />}
    </>
  );
};

export default Report;